import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // CRO FIX 2026-09-07: keep the Shop Pay installments widget in sync with the
    // selected variant.
    //
    // The `.installments` block rendered by blocks/price.liquid holds a hidden
    // `id` input and a <shopify-payment-terms> element whose `variant-id`
    // attribute drives the figures Shopify renders inside it. Nothing in this
    // handler used to touch that block, so it kept whichever variant was
    // rendered on page load and advertised that variant's monthly figure no
    // matter what the shopper selected - e.g. a $382 length still showing the
    // $145 length's "from $13.09/mo".
    //
    // The node is REPLACED rather than having its attribute rewritten: Shopify's
    // component reads variant-id when it initialises, so a fresh element is what
    // makes it re-render. Every branch is null-safe because this same component
    // also runs on product cards, where `.installments` never exists.
    const newInstallments = newProductPrice.querySelector('.installments');
    const currentInstallments = this.querySelector('.installments');

    if (newInstallments && currentInstallments) {
      currentInstallments.replaceWith(newInstallments);
    } else if (currentInstallments) {
      currentInstallments.remove();
    } else if (newInstallments) {
      this.appendChild(newInstallments);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
